import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errMsg, fmtBtc, fmtUsd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ExportCsvButton } from "@/components/ExportCsvButton";

export const DeleteAccount = () => {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const hasFunds = (user?.usd_balance || 0) > 0.009 || (user?.btc_balance || 0) > 0;

  const remove = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete", { confirm });
      setUser(null);
      toast.success("Your account has been deleted");
      nav("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="delete-account-section">
      <p className="text-sm text-[#94A3B8]">Permanently delete your account, alerts, auto-buy plans, notifications and support chats. Card subscriptions are cancelled. This can't be undone.</p>
      {hasFunds && (
        <div className="mt-3 flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="delete-account-funds-warning">
          <AlertTriangle size={16} className="shrink-0" />
          <span>You still have <span className="font-mono">{fmtUsd(user.usd_balance)}</span> and <span className="font-mono">{fmtBtc(user.btc_balance)} BTC</span>. Sell all your BTC and withdraw your full USD balance on the dashboard before you can delete your account.</span>
        </div>
      )}
      <div className="mt-4 rounded-xl border border-[#212638] bg-[#0F111A] p-3">
        <p className="mb-2 text-xs text-[#94A3B8]">Download your full transaction history first — it can't be recovered after deletion.</p>
        <ExportCsvButton testid="delete-export-csv-button" />
      </div>
      <AlertDialog onOpenChange={(o) => !o && setConfirm("")}>
        <AlertDialogTrigger asChild>
          <button disabled={hasFunds} data-testid="delete-account-open-button" className="mt-4 disabled:cursor-not-allowed disabled:opacity-40 flex items-center gap-2 rounded-xl border border-red-500/50 px-4 py-2.5 text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/10">
            <Trash2 size={15} /> Delete account
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="border-[#212638] bg-[#151824] text-[#F8FAFC]" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">This is permanent. Type <span className="font-mono font-semibold text-red-400">DELETE</span> to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="delete-account-confirm-input"
            className="w-full rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5 font-mono text-sm outline-none transition-colors focus:border-red-500" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel-button" className="border-[#212638] bg-transparent text-[#F8FAFC] hover:bg-[#1C2030] hover:text-white">Cancel</AlertDialogCancel>
            <button onClick={remove} disabled={confirm !== "DELETE" || busy} data-testid="delete-account-confirm-button"
              className="flex items-center justify-center gap-2 rounded-md bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-400 disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} Delete forever
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
